/**
 * ResultStep — final counts + failure detail + undo pointer.
 */

import { Button } from '@/components/ui/button';
import type { ImportProgress } from './ImportProgressStep';

const FAILURE_RENDER_CAP = 50;

interface Props {
  progress: ImportProgress;
  onImportMore: () => void;
  onViewHistory: () => void;
}

export function ResultStep({ progress, onImportMore, onViewHistory }: Props) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
            {progress.created.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">imported</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-2xl font-semibold">{progress.skipped.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">skipped (duplicates)</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p
            className={
              progress.failed.length > 0
                ? 'text-2xl font-semibold text-destructive'
                : 'text-2xl font-semibold'
            }
          >
            {progress.failed.length.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">failed</p>
        </div>
      </div>

      {progress.failed.length > 0 && (
        <details className="rounded-lg border border-border bg-card p-3">
          <summary className="cursor-pointer text-sm text-muted-foreground">
            Show failed rows
          </summary>
          <ul className="mt-2 max-h-48 overflow-y-auto text-xs space-y-0.5">
            {progress.failed.slice(0, FAILURE_RENDER_CAP).map((f) => (
              <li key={f.rowIndex} className="text-destructive">
                Row {f.rowIndex + 1}: {f.error}
              </li>
            ))}
            {progress.failed.length > FAILURE_RENDER_CAP && (
              <li className="text-muted-foreground">
                … and {progress.failed.length - FAILURE_RENDER_CAP} more
              </li>
            )}
          </ul>
        </details>
      )}

      <p className="text-sm text-muted-foreground">
        Imported by mistake? You can archive this whole import (and restore it later) from Import
        History.
      </p>

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onImportMore}>
          Import more
        </Button>
        <Button type="button" variant="ghost" onClick={onViewHistory}>
          View import history
        </Button>
      </div>
    </div>
  );
}
